import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';

import { AppPagination } from '@/__core/components';
import ConfirmationModal from '@/__core/components/ConfirmationModal';

import DataTableModule from '@/__core/layout/module/DataTableModule';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';

import { InventoryItemRow } from '../models/ItemModel';

export type ItemListHandle = {
  requestRefresh: () => void;
  requestDelete: () => void;
};

interface ItemListProps {
  rows: InventoryItemRow[];
  loading?: boolean;
  pagination?: {
    count: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  onPageChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  onSelectRow?: (row: InventoryItemRow) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  onBulkDelete?: (itemIds: string[]) => void | Promise<void>;
  onBulkStatusChange?: (itemIds: string[], status: 'draft' | 'approved' | 'inactive') => void | Promise<void>;
  canUpdate?: boolean;
  canDelete?: boolean;
  onRefresh?: () => void;
}

const statusColor = (status: string): 'default' | 'warning' | 'success' | 'info' | 'error' => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'draft':
      return 'warning';
    case 'inactive':
      return 'default';
    default:
      return 'default';
  }
};

const ItemList = forwardRef<ItemListHandle, ItemListProps>(function ItemList(
  {
    rows,
    loading = false,
    pagination,
    onPageChange,
    onSelectRow,
    onSelectionChange,
    onBulkDelete,
    onBulkStatusChange,
    canUpdate = true,
    canDelete = true,
    onRefresh,
  },
  ref
) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState('');
  const [applyingAction, setApplyingAction] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const canBulkSelect = canUpdate || canDelete;

  useEffect(() => {
    const rowIds = new Set(rows.map((row) => row.id));
    setSelectedIds((current) => current.filter((id) => rowIds.has(id)));
  }, [rows]);

  useEffect(() => {
    onSelectionChange?.(selectedIds);
  }, [onSelectionChange, selectedIds]);

  const openDeleteConfirm = useCallback(() => {
    if (!canDelete || selectedIds.length === 0) {
      return;
    }
    setConfirmDeleteOpen(true);
  }, [canDelete, selectedIds.length]);

  useImperativeHandle(
    ref,
    () => ({
      requestRefresh: () => onRefresh?.(),
      requestDelete: openDeleteConfirm,
    }),
    [onRefresh, openDeleteConfirm]
  );

  const handleConfirmDelete = async () => {
    if (!canDelete || selectedIds.length === 0) {
      setConfirmDeleteOpen(false);
      return;
    }

    try {
      setApplyingAction(true);
      await Promise.resolve(onBulkDelete?.([...selectedIds]));
      setSelectedIds([]);
      setBulkAction('');
    } finally {
      setApplyingAction(false);
      setConfirmDeleteOpen(false);
    }
  };

  const areAllRowsSelected = useMemo(() => rows.length > 0 && selectedIds.length === rows.length, [rows.length, selectedIds.length]);
  const areSomeRowsSelected = useMemo(() => selectedIds.length > 0 && selectedIds.length < rows.length, [rows.length, selectedIds.length]);

  const handleToggleRow = (id: string) => {
    if (!canBulkSelect) {
      return;
    }
    setSelectedIds((current) => (current.includes(id) ? current.filter((itemId) => itemId !== id) : [...current, id]));
  };

  const handleToggleAll = () => {
    if (!canBulkSelect) {
      return;
    }
    if (areAllRowsSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(rows.map((row) => row.id));
  };

  const handleApplyBulkAction = async () => {
    if (!bulkAction || selectedIds.length === 0) {
      return;
    }

    try {
      setApplyingAction(true);

      if (bulkAction === 'delete') {
        if (!canDelete) {
          return;
        }
        await onBulkDelete?.(selectedIds);
      } else if (bulkAction === 'status_draft') {
        if (!canUpdate) {
          return;
        }
        await onBulkStatusChange?.(selectedIds, 'draft');
      } else if (bulkAction === 'status_approved') {
        if (!canUpdate) {
          return;
        }
        await onBulkStatusChange?.(selectedIds, 'approved');
      } else if (bulkAction === 'status_inactive') {
        if (!canUpdate) {
          return;
        }
        await onBulkStatusChange?.(selectedIds, 'inactive');
      }

      setSelectedIds([]);
      setBulkAction('');
    } finally {
      setApplyingAction(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, height: 220 }}>
        <Typography variant='body1' color='text.secondary'>Không có vật tư nào</Typography>
        <Typography variant='body2' color='text.secondary'>Thử thay đổi bộ lọc hoặc tạo vật tư mới.</Typography>
      </Box>
    );
  }

  const columns = [
    { id: 'code', label: 'Mã vật tư', width: '160px', render: (_: unknown, row: InventoryItemRow) => <Typography sx={{ fontWeight: 600 }}>{row.code}</Typography> },
    { id: 'name', label: 'Tên vật tư', width: '280px' },
    { id: 'supplier_name', label: 'Nhà cung cấp', width: '220px', render: (_: unknown, row: InventoryItemRow) => row.supplier_name || '-' },
    { id: 'manufacturer_name', label: 'Nhà sản xuất', width: '220px', render: (_: unknown, row: InventoryItemRow) => row.manufacturer_name || '-' },
    { id: 'uom_name', label: 'ĐVT', width: '120px', render: (_: unknown, row: InventoryItemRow) => row.uom_name || '-' },
  ];

  const totalCount = pagination?.count || rows.length;
  const currentPage = pagination?.page || 1;
  const pageSize = pagination?.pageSize || rows.length || DEFAULT_PAGE_SIZE;
  const totalPages = pagination?.totalPages || 1;

  return (
    <Box sx={sharedListStyles.container}>
      <DataTableModule
        data={rows}
        columns={columns}
        leftFilterWidth={0}
        onRowSelect={handleToggleRow}
        onSelectAll={handleToggleAll}
        onRowClick={(row) => onSelectRow?.(row as InventoryItemRow)}
        getRowId={(row) => (row as InventoryItemRow).id}
        isRowSelected={(row) => selectedIds.includes((row as InventoryItemRow).id)}
        areAllRowsSelected={areAllRowsSelected}
        areSomeRowsSelected={areSomeRowsSelected}
        showCheckbox={canBulkSelect}
      />
      {selectedIds.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            px: 2,
            py: 2,
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
            <Typography variant='body2' sx={{ color: '#374151' }}>
              Đã chọn {selectedIds.length} vật tư
            </Typography>
            <TextField
              select
              size='small'
              value={bulkAction}
              onChange={(event) => setBulkAction(event.target.value)}
              disabled={applyingAction}
              sx={{ minWidth: 260 }}
            >
              <MenuItem value=''>Chọn action cho items đã chọn</MenuItem>
              {canDelete ? <MenuItem value='delete'>Delete</MenuItem> : null}
              {canUpdate ? <MenuItem value='status_draft'>Chuyen nhap</MenuItem> : null}
              {canUpdate ? <MenuItem value='status_approved'>Chuyen duyet</MenuItem> : null}
              {canUpdate ? <MenuItem value='status_inactive'>Ngung su dung</MenuItem> : null}
            </TextField>
            <Button
              variant='contained'
              size='small'
              onClick={() => { void handleApplyBulkAction(); }}
              disabled={!bulkAction || applyingAction}
              sx={{ minWidth: 84, height: 32, px: 1.5 }}
            >
              {applyingAction ? 'Đang xử lý...' : 'Áp dụng'}
            </Button>
          </Box>
        </Box>
      )}
      <AppPagination
        count={totalCount}
        page={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        itemLabel='vật tư'
        onPageChange={onPageChange}
      />

      <ConfirmationModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={() => { void handleConfirmDelete(); }}
        title='Xóa vật tư'
        message={`Bạn có chắc muốn xóa ${selectedIds.length} vật tư đã chọn?`}
        confirmText='Xóa'
        cancelText='Hủy'
        type='danger'
        icon='🗑️'
      />
    </Box>
  );
});

export default ItemList;